import { useCallback, useEffect, useMemo, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { CloudUpload, FileIcon, FileText, X } from 'lucide-react';

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Renders a size cap in the friendliest unit: whole GB once it reaches 1 GB
// (e.g. the 4768 MB media cap reads "5 GB"), otherwise plain MB.
function formatSizeLimit(mb: number): string {
  if (mb >= 1024) {
    return `${Math.round(mb / 1024)} GB`;
  }
  return `${mb} MB`;
}
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/loading-spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SourceType } from '@/types/source-types';
import { DropzoneErrorAlert } from '@/components/dropzone-error-alert';
import { cn } from '@/lib/utils';

interface DropzoneProps {
  // Auto-upload mode props (legacy - for backward compatibility)
  handleUpload?: (files: File[]) => void;

  // Manual mode props
  onFilesChange?: (files: File[]) => void;

  // Duplicate validation props
  existingFiles?: Array<{ name: string }>;

  // File name regex props
  fileNameRegex?: RegExp;

  // File name error message props
  fileNameErrorMessage?: string;

  // Common props
  acceptType?: Accept;
  maxSize?: number; // in MB
  /** Larger cap for audio and video files (in MB). Other files stay under `maxSize`. */
  audioMaxSize?: number; // in MB
  multiple?: boolean;
  preventCanvasInterference?: boolean;

  // Mode selection - defaults to 'auto' for backward compatibility
  mode?: 'auto' | 'manual';

  // Loading state and message
  isLoading?: boolean;
  loadingMessage?: string;
}

// Media types from FILE_INPUT_ACCEPT — used as MIME fallback in the per-type
// size validator; video gets the same large-file cap as audio.
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'm4a']);
const VIDEO_EXTENSIONS = new Set(['mp4']);

export const AcceptType = {
  // Each MIME needs its own extensions: drag-and-drop matches on MIME, so a
  // shared list would reject JPEGs.
  IMAGE: {
    [SourceType.PNG]: ['.png'],
    [SourceType.JPEG]: ['.jpg', '.jpeg'],
  },
  RICH_TEXT: { [SourceType.RICH_TEXT]: ['.rtf'] },
  PDF: { [SourceType.PDF]: ['.pdf'] },
  DOCUMENT: {
    [SourceType.MSWORD]: ['.doc'],
    [SourceType.MSWORDX]: ['.docx'],
  },
  PRESENTATION: {
    [SourceType.POWERPOINT]: ['.ppt'],
    [SourceType.POWERPOINTX]: ['.pptx'],
  },
  SPREADSHEET: {
    [SourceType.EXCEL]: ['.xls'],
    [SourceType.SHEET]: ['.xlsx'],
    [SourceType.CSV]: ['.csv'],
    [SourceType.PARQUET]: ['.parquet'],
  },
  MARKDOWN: { [SourceType.MARKDOWN]: ['.md'] },
  TEXT: { [SourceType.TEXT]: ['.txt'] },
  JSON: { [SourceType.JSON]: ['.json'] },
  /** `.skill` bundles are ZIP on disk; OS often reports `application/zip`, so allow `.skill` here too */
  ZIP: {
    [SourceType.ZIP]: ['.zip', '.skill'],
    [SourceType.ZIP_COMPRESSED]: ['.zip', '.skill'],
  },
  ALL: { '*': ['Any Type'] },
} as const;

const checkForDuplicates = (
  acceptedFiles: File[],
  existingFiles: Array<{ name: string }>,
) => {
  const duplicates: File[] = [];
  const nonDuplicates: File[] = [];

  acceptedFiles.forEach((file) => {
    if (existingFiles.some((existing) => existing.name === file.name)) {
      duplicates.push(file);
    } else {
      nonDuplicates.push(file);
    }
  });
  return { duplicates, nonDuplicates };
};

export const Dropzone = ({
  // Auto-upload mode props
  handleUpload,

  // Manual mode props
  onFilesChange,

  // Duplicate validation props
  existingFiles = [],

  // File name regex props
  fileNameRegex,

  // File name error message props
  fileNameErrorMessage,

  // Common props
  acceptType,
  maxSize,
  audioMaxSize,
  multiple = true,
  preventCanvasInterference = false,
  mode = 'auto', // Default to auto for backward compatibility

  // Loading state
  isLoading = false,
  loadingMessage,
}: DropzoneProps) => {
  // Manual mode state
  const [files, setFiles] = useState<File[]>([]);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  // Auto mode: show a clear message when user drops/selects an unsupported file
  const [autoModeRejectionMessage, setAutoModeRejectionMessage] = useState<
    string | null
  >(null);

  // True when a single file has been staged in manual single-file mode
  const singleFileSelected = mode === 'manual' && !multiple && files.length > 0;
  const singleFile = singleFileSelected ? files[0] : null;

  // Flat, deduplicated list of extensions derived from acceptType for pill rendering
  const extensions = useMemo(
    () =>
      acceptType
        ? [
            ...new Set(
              Object.values(acceptType as Record<string, string[]>).flat(),
            ),
          ]
        : [],
    [acceptType],
  );

  // Clear auto mode rejection message on unmount or when switching modes
  useEffect(() => {
    return () => {
      setAutoModeRejectionMessage(null);
    };
  }, []);

  useEffect(() => {
    if (multiple || files.length <= 1) return;

    const [first] = files;
    setFiles(first ? [first] : []);
    onFilesChange?.(first ? [first] : []);
  }, [multiple, files, onFilesChange]);

  const updateFiles = useCallback(
    (next: File[]) => {
      setFiles(next);
      onFilesChange?.(next);
    },
    [onFilesChange],
  );

  const getFileRejectionMessage = useCallback(
    (errorCode: string, maxSize?: number): string => {
      switch (errorCode) {
        case 'file-invalid-type':
          return 'This file type is not supported. Please choose an accepted file type.';
        case 'too-many-files':
          return 'Only one file is allowed. Please select a single file.';
        case 'file-too-large':
          return typeof maxSize === 'number'
            ? `File is larger than ${formatSizeLimit(maxSize)}.`
            : 'File is too large.';
        case 'media-too-large':
          return typeof maxSize === 'number'
            ? `Files must be smaller than ${formatSizeLimit(maxSize)}.`
            : 'File exceeds the allowed size.';
        default:
          return 'This file could not be uploaded.';
      }
    },
    [],
  );

  const effectiveMaxSizeMB =
    typeof audioMaxSize === 'number' && typeof maxSize === 'number'
      ? Math.max(audioMaxSize, maxSize)
      : (audioMaxSize ?? maxSize);

  const perTypeValidator = useCallback(
    (file: File) => {
      if (typeof audioMaxSize !== 'number' || typeof maxSize !== 'number') {
        return null;
      }
      // react-dropzone runs this validator during drag too, where the browser
      // hasn't exposed file contents yet and `name`/`size` may be absent.
      // Guard on size (as react-dropzone itself does via isDefined) so we
      // don't throw on `file.name.split`; the real check runs again at drop.
      if (typeof file.size !== 'number') {
        return null;
      }
      // Browsers may report empty/generic MIME for audio files (e.g. .m4a);
      // fall back to extension so they aren't wrongly rejected at the lower cap.
      const mime = file.type || '';
      const ext = (file.name || '').split('.').pop()?.toLowerCase() ?? '';
      const isAudioMime = mime.startsWith('audio/');
      const isAudioExt = AUDIO_EXTENSIONS.has(ext);
      const isVideoMime = mime.startsWith('video/');
      const isVideoExt = VIDEO_EXTENSIONS.has(ext);
      if (isAudioMime || isAudioExt || isVideoMime || isVideoExt) {
        return null;
      }
      if (file.size > maxSize * 1024 * 1024) {
        return {
          code: 'media-too-large',
          message: `Files must be smaller than ${formatSizeLimit(maxSize)}.`,
        };
      }
      return null;
    },
    [audioMaxSize, maxSize],
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (mode === 'manual') {
        // 1. First validate filenames on ALL accepted files
        const invalidNameFiles = fileNameRegex
          ? acceptedFiles.filter((file) => !file.name.match(fileNameRegex))
          : [];

        // 2. Get files with valid names
        const validNameFiles = fileNameRegex
          ? acceptedFiles.filter((file) => file.name.match(fileNameRegex))
          : acceptedFiles;

        // 3. Check for duplicates against existing files (only for valid name files)
        const { duplicates, nonDuplicates } = checkForDuplicates(
          validNameFiles,
          existingFiles,
        );

        // 4. Add all rejections
        setRejections((prev) => [
          ...prev,
          ...fileRejections,
          ...invalidNameFiles.map((file) => ({
            file,
            errors: [
              {
                code: 'file-name-invalid',
                message:
                  fileNameErrorMessage ||
                  'File name contains invalid characters',
              },
            ],
          })),
          ...duplicates.map((file) => ({
            file,
            errors: [
              {
                code: 'file-duplicate',
                message: 'File already exists',
              },
            ],
          })),
        ]);

        // 5. Early return if no valid files
        if (nonDuplicates.length === 0) return;

        // 6. Handle multiple vs single file logic
        const incoming = multiple ? nonDuplicates : [nonDuplicates[0]];
        const combined = multiple ? [...files, ...incoming] : incoming;

        // 7. Remove internal duplicates using name-size key
        const unique = new Map<string, File>();
        for (const f of combined) {
          unique.set(`${f.name}-${f.size}`, f);
        }
        updateFiles(Array.from(unique.values()));
      } else {
        // Auto mode: immediate upload (legacy behavior)
        if (fileRejections.length > 0 && acceptedFiles.length === 0) {
          const firstError = fileRejections[0]?.errors?.[0];
          const code = firstError?.code ?? '';
          const limitForMessage =
            code === 'file-too-large' ? effectiveMaxSizeMB : maxSize;
          const message = getFileRejectionMessage(code, limitForMessage);
          setAutoModeRejectionMessage(message);
          return;
        }
        setAutoModeRejectionMessage(null);
        if (acceptedFiles.length === 0) return;

        handleUpload?.(acceptedFiles);
      }
    },
    [
      mode,
      multiple,
      maxSize,
      effectiveMaxSizeMB,
      files,
      updateFiles,
      handleUpload,
      existingFiles,
      fileNameRegex,
      fileNameErrorMessage,
      getFileRejectionMessage,
    ],
  );

  const removeFile = useCallback(
    (fileToRemove: File) => {
      updateFiles(
        files.filter(
          (f) =>
            !(f.name === fileToRemove.name && f.size === fileToRemove.size),
        ),
      );
    },
    [files, updateFiles],
  );

  const clearAll = useCallback(() => updateFiles([]), [updateFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptType as Accept,
    onDrop,
    multiple,
    maxFiles: multiple ? undefined : 1,
    maxSize:
      typeof effectiveMaxSizeMB === 'number'
        ? effectiveMaxSizeMB * 1024 * 1024
        : undefined,
    validator: perTypeValidator,
    // Clicking an already-loaded single file zone does nothing — the clear
    // button handles removal.
    noClick: singleFileSelected,
    noKeyboard: false,
    noDragEventsBubbling: preventCanvasInterference,
  });

  const formatRejectionError = useCallback(
    (message: string, code?: string) => {
      if (code === 'file-too-large' && typeof effectiveMaxSizeMB === 'number') {
        return `File is larger than ${formatSizeLimit(effectiveMaxSizeMB)}`;
      }
      if (code === 'media-too-large' && typeof maxSize === 'number') {
        return `Files must be smaller than ${formatSizeLimit(maxSize)}`;
      }
      return message.replace(/(\d+(?:\.\d+)?)\s*bytes/gi, (_, n) => {
        const mb = Number(n) / (1024 * 1024);
        return `${mb.toFixed(0)} MB`;
      });
    },
    [maxSize, effectiveMaxSizeMB],
  );

  const TruncatedFileName = ({ fileName }: { fileName: string }) => {
    const shouldTruncate = fileName.length > 25;

    if (!shouldTruncate) {
      return <span className='text-sm'>{fileName}</span>;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='text-sm truncate cursor-help overflow-hidden whitespace-nowrap'>
              {fileName}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{fileName}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className='flex flex-col gap-3'>
      {isLoading ? (
        <div className='flex flex-col items-center justify-center py-8 gap-3 border rounded-md border-dashed w-full bg-background border-border'>
          <LoadingSpinner />
          <span className='text-sm text-muted-foreground'>
            {loadingMessage ??
              (multiple ? 'Uploading files...' : 'Uploading file...')}
          </span>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            'border rounded-md w-full p-5 transition-colors duration-150',
            singleFileSelected
              ? 'border-solid border-primary bg-background cursor-default'
              : cn(
                  'border-dashed border-border bg-muted/50 cursor-pointer',
                  'hover:border-primary hover:bg-background',
                  isDragActive && 'border-primary bg-background',
                ),
          )}
        >
          <input {...getInputProps()} />

          {singleFileSelected ? (
            // ── Selected state: file row rendered in-place ──────────────────
            <div className='flex items-center gap-[10px]'>
              {/* File icon tile */}
              <div className='shrink-0 size-8 rounded border border-primary/30 bg-primary/10 flex items-center justify-center'>
                <FileText
                  size={15}
                  strokeWidth={1.5}
                  className='text-primary'
                />
              </div>

              {/* File info */}
              <div className='flex-1 min-w-0'>
                <p className='text-[13px] font-medium truncate text-foreground'>
                  {singleFile!.name}
                </p>
                <p className='text-[11px] font-mono text-muted-foreground'>
                  {formatFileSize(singleFile!.size)}
                </p>
              </div>

              {/* Clear button */}
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='shrink-0 size-7'
                onClick={(e) => {
                  e.stopPropagation();
                  updateFiles([]);
                }}
                aria-label='Remove file'
              >
                <X size={14} />
              </Button>
            </div>
          ) : (
            // ── Idle state ──────────────────────────────────────────────────
            <div className='flex flex-col items-center justify-center gap-2'>
              {/* Upload icon */}
              <CloudUpload size={24} className='text-muted-foreground' />

              {/* CTA */}
              <p className='text-[13px] text-center text-foreground leading-tight'>
                <span className='underline text-primary'>Click to upload</span>{' '}
                or drag and drop
              </p>

              {/* Constraint */}
              <p className='text-[11px] text-muted-foreground'>
                {multiple ? 'Multiple files' : 'Single file'}
                {typeof audioMaxSize === 'number' && typeof maxSize === 'number'
                  ? ` · ${formatSizeLimit(maxSize)} max (audio/video: ${formatSizeLimit(audioMaxSize)})`
                  : maxSize != null
                    ? ` · ${formatSizeLimit(maxSize)} max`
                    : ''}
              </p>

              {/* Extension pills */}
              {extensions.length > 0 && (
                <div className='flex flex-wrap justify-center gap-1 mt-1'>
                  {extensions.map((ext) => (
                    <span
                      key={ext}
                      className='font-mono text-[10px] bg-background border border-border/60 text-muted-foreground px-[5px] py-px'
                      style={{ borderRadius: 4 }}
                    >
                      {ext}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Auto mode: rejection alert */}
      {!isLoading && mode === 'auto' && autoModeRejectionMessage && (
        <DropzoneErrorAlert message={autoModeRejectionMessage} />
      )}

      {/* Manual mode: rejection list */}
      {!isLoading && mode === 'manual' && rejections.length > 0 && (
        <div className='flex flex-col gap-2'>
          <span className='text-sm font-medium text-red-600 dark:text-red-400'>
            Rejected file{rejections.length > 1 ? 's' : ''}
          </span>
          <div className='rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-3'>
            <ul className='flex flex-col max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-red-300 [&::-webkit-scrollbar-thumb]:rounded-full dark:[&::-webkit-scrollbar-thumb]:bg-red-600'>
              {rejections.map((rej, index) => (
                <li
                  key={`${rej.file.name}-${rej.file.size}`}
                  className={cn(
                    'flex items-start justify-between min-w-0 py-2',
                    index < rejections.length - 1 &&
                      'border-b border-red-200 dark:border-red-800',
                  )}
                >
                  <div className='flex flex-col gap-1 min-w-0 w-full'>
                    <div className='flex items-center gap-2 min-w-0 flex-1'>
                      <FileIcon size={16} className='shrink-0' />
                      <div className='min-w-0 flex-1'>
                        <TruncatedFileName fileName={rej.file.name} />
                      </div>
                      <span className='text-xs text-muted-foreground shrink-0'>
                        ({formatFileSize(rej.file.size)})
                      </span>
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      {rej.errors
                        .map((e) => formatRejectionError(e.message, e.code))
                        .join(', ')}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Manual mode: multiple-file list (single-file mode shows the file in-place above) */}
      {!isLoading && mode === 'manual' && multiple && files.length > 0 && (
        <div className='flex flex-col gap-2'>
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium text-muted-foreground'>
              Selected file{files.length > 1 ? 's' : ''}
            </span>
            <Button variant='ghost' size='sm' onClick={clearAll}>
              Clear all
            </Button>
          </div>
          <div className='rounded-md border border-input bg-background p-3'>
            <ul className='flex flex-col max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full dark:[&::-webkit-scrollbar-thumb]:bg-gray-600'>
              {files.map((f, index) => (
                <li
                  key={`${f.name}-${f.size}`}
                  className={cn(
                    'flex items-center justify-between min-w-0 py-2',
                    index < files.length - 1 && 'border-b border-border',
                  )}
                >
                  <div className='flex items-center gap-2 min-w-0 flex-1'>
                    <FileIcon size={16} className='shrink-0' />
                    <div className='min-w-0 flex-1'>
                      <TruncatedFileName fileName={f.name} />
                    </div>
                    <span className='text-xs text-muted-foreground shrink-0'>
                      ({formatFileSize(f.size)})
                    </span>
                  </div>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => removeFile(f)}
                    aria-label={`Remove ${f.name}`}
                  >
                    <X size={16} />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
